import { useMemo, useState } from "react";

import {
  Box,
  Button,
  Heading,
  HStack,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  PinInput,
  PinInputField,
  Text
} from "@chakra-ui/react";

import { useDispatch } from "react-redux";
import { useTimer } from "react-timer-hook";

import {
  FeatureType,
  WhatsappIntegrationStages
} from "@/jenesys-types/data/enums";

import useActionTrigger from "@/hooks/util/useActionTrigger";
import { loadWhatsappStatus } from "@/store/features/clientSettings/connections/connections";
import { toastError } from "@/util/helpers";

interface VerifyWhatsappNumberProps {
  code: string;
  setCode: (code: string) => void;
  phone: string;
  setStep: (step: WhatsappIntegrationStages) => void;
  clientId: string | undefined;
}

export const VerifyWhatsappNumber: React.FC<VerifyWhatsappNumberProps> = ({
  code,
  setCode,
  phone,
  setStep
}) => {
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch();

  const expiryTime = useMemo(() => {
    const time = new Date();
    time.setSeconds(time.getSeconds() + 60);
    return time;
  }, []);

  const { seconds, minutes, isRunning, restart } = useTimer({
    autoStart: true,
    expiryTimestamp: expiryTime
  });

  const { trigger: submitOTPForVerification, isLoading: isVerifyingOTP } =
    useActionTrigger({
      actionName: "verifyWhatsappOTP",
      data: {
        phone_number: `+${phone}`,
        otp: code
      },
      featureCluster: FeatureType.Integrations,
      onSuccess: () => {
        setStep(WhatsappIntegrationStages.SUCCESS);
        dispatch(loadWhatsappStatus());
      },
      onError: (error) => {
        toastError("Unable to verify whatsapp number", error);
      }
    });

  const { trigger: requestOTPResend, isLoading: isRequestingResend } =
    useActionTrigger({
      actionName: "requestOTPResend",
      params: {
        number: `%2B${phone}`
      },
      featureCluster: FeatureType.Integrations,
      onSuccess: () => {
        setCode("");
        setShowError(false);
        const nextExpiryTime = new Date();
        nextExpiryTime.setSeconds(nextExpiryTime.getSeconds() + 60);
        restart(nextExpiryTime);
      },
      onError: (error) => {
        toastError("Unable to request OTP resend", error);
      }
    });

  const changePhoneNumber = () => {
    setStep(WhatsappIntegrationStages.ENTER_NUMBER);
    setCode("");
  };

  return (
    <ModalContent rounded={16} as="form" fontWeight={500} p={12}>
      <ModalHeader p={0}>
        <Heading fontSize={20} color="secondary.600" fontWeight={600}>
          Verify your WhatsApp number
        </Heading>
      </ModalHeader>
      <ModalCloseButton top={10} right={10} />
      <ModalBody pt={5} px={0}>
        <Text mb={5} fontSize={14}>
          Enter the 6-digit verification code that was sent to +{phone}.
          <Link
            onClick={changePhoneNumber}
            ml={2}
            color="blue.500"
            textDecor="underline"
          >
            Change
          </Link>
        </Text>

        <HStack mt={11}>
          <PinInput
            autoFocus
            value={code}
            onChange={setCode}
            placeholder=" "
            size="lg"
            otp
          >
            <PinInputField />
            <PinInputField />
            <PinInputField />
            <PinInputField />
            <PinInputField />
            <PinInputField />
          </PinInput>

          <Button
            w={190}
            h={12}
            isLoading={isVerifyingOTP}
            loadingText="Verifying..."
            colorScheme="primary"
            onClick={() => submitOTPForVerification()}
          >
            Verify Account
          </Button>
        </HStack>
        {!showError && (
          <Box mt={6} fontSize={14} fontWeight={500}>
            {isRunning && (
              <Text>
                The OTP will expire in&nbsp;
                <Box fontWeight="bold" as="span">
                  {/* Show seconds always in 2-digit format */}
                  {minutes}:{String(seconds).padStart(2, "0")}
                </Box>
              </Text>
            )}
            <Text>
              Didn&apos;t receive the code?&nbsp;
              <Button
                size="sm"
                onClick={() => requestOTPResend()}
                isDisabled={isRunning}
                isLoading={isRequestingResend}
                loadingText="Resending..."
                variant="link"
                fontWeight={600}
                color="blue.500"
                textDecor="underline"
              >
                Resend
              </Button>
            </Text>
          </Box>
        )}

        {showError && (
          <Box mt={6} fontSize={14} fontWeight={500}>
            <Text color="red.500">
              Not verified. Check and re-enter your code.
            </Text>
            <Button
              onClick={() => /* requestResendMutate() */ {}}
              isDisabled={isRunning}
              isLoading={/* isRequestingResend */ false}
              loadingText="Resending..."
              variant="link"
              size="sm"
              fontWeight={600}
              color="blue.500"
              textDecor="underline"
            >
              or Request new code
            </Button>
          </Box>
        )}
      </ModalBody>
    </ModalContent>
  );
};
